import React, { useEffect, useState, useRef } from "react";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Navigate } from "react-router-dom";
import { Send, Loader2, Sparkles, MessageSquarePlus, ChevronLeft, MapPin, Calendar as CalIcon } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function newSessionId() {
  return "chat-" + Math.random().toString(36).slice(2, 12) + "-" + Date.now().toString(36);
}

// Minimal SSE parser for our text/event-stream response
async function* parseSSE(reader) {
  const decoder = new TextDecoder("utf-8");
  let buf = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buf.indexOf("\n\n")) >= 0) {
      const rawEvent = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      let ev = "message", data = "";
      for (const line of rawEvent.split("\n")) {
        if (line.startsWith("event:")) ev = line.slice(6).trim();
        else if (line.startsWith("data:")) data += line.slice(5).trim();
      }
      if (!data) continue;
      try { yield { event: ev, data: JSON.parse(data) }; }
      catch { /* ignore parse issues */ }
    }
  }
}

export default function ChatPage() {
  const { user, loading } = useAuth();
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("eventom_chat_session") || newSessionId());
  const [sessions, setSessions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const listRef = useRef(null);

  useEffect(() => { localStorage.setItem("eventom_chat_session", sessionId); }, [sessionId]);

  const loadSessions = () => api.get("/chat/sessions").then(({ data }) => setSessions(data)).catch(() => {});
  const loadHistory = (sid) => api.get(`/chat/${sid}/history`).then(({ data }) => setMessages(data.messages || [])).catch(() => setMessages([]));

  useEffect(() => { if (user) { loadSessions(); loadHistory(sessionId); } }, [user, sessionId]);
  useEffect(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, [messages]);

  if (loading) return <Layout><div className="p-10">Loading...</div></Layout>;
  if (!user) return <Navigate to="/login" replace />;

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim(); if (!text || busy) return;
    setBusy(true);
    const now = new Date().toISOString();
    // Optimistic user turn + placeholder assistant that we'll stream into
    setMessages((m) => [...m, { role: "user", content: text, created_at: now },
                        { role: "assistant", content: "", events: [], created_at: new Date().toISOString(), streaming: true }]);
    setInput("");

    const token = localStorage.getItem("eventom_token");
    try {
      const res = await fetch(`${BACKEND}/api/chat/stream`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (!res.ok || !res.body) {
        // Fallback to non-streaming endpoint
        const { data } = await api.post("/chat", { session_id: sessionId, message: text });
        setMessages((m) => {
          const copy = m.slice(0, -1);
          copy.push({ role: "assistant", content: data.reply, events: data.events || [], created_at: new Date().toISOString(), model: data.model });
          return copy;
        });
        loadSessions();
        return;
      }
      const reader = res.body.getReader();
      let accEvents = [];
      let accText = "";
      for await (const evt of parseSSE(reader)) {
        if (evt.event === "events" && Array.isArray(evt.data?.events)) {
          accEvents = evt.data.events;
          setMessages((m) => {
            const copy = [...m];
            const last = copy[copy.length - 1];
            if (last && last.streaming) copy[copy.length - 1] = { ...last, events: accEvents };
            return copy;
          });
        } else if (evt.event === "delta" && typeof evt.data?.t === "string") {
          accText += evt.data.t;
          setMessages((m) => {
            const copy = [...m];
            const last = copy[copy.length - 1];
            if (last && last.streaming) copy[copy.length - 1] = { ...last, content: accText };
            return copy;
          });
        } else if (evt.event === "error") {
          throw new Error(evt.data?.error || "stream error");
        } else if (evt.event === "done") {
          setMessages((m) => {
            const copy = [...m];
            const last = copy[copy.length - 1];
            if (last && last.streaming) copy[copy.length - 1] = { ...last, streaming: false };
            return copy;
          });
        }
      }
      loadSessions();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message || "Chat failed");
      // Rollback the streaming placeholder + user turn
      setMessages((m) => m.slice(0, -2));
      setInput(text);
    } finally { setBusy(false); }
  };

  const newChat = () => { const sid = newSessionId(); setSessionId(sid); setMessages([]); setShowSidebar(false); };

  return (
    <Layout>
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-6 h-[calc(100vh-160px)] flex flex-col" data-testid="chat-page">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="chip bg-skyx/20 text-skyx mb-1">Eventom Genie · GPT-5.6 Sol</div>
            <h1 className="font-display uppercase text-3xl md:text-4xl leading-tight">Chat with <span className="grad-text-hot">Genie.</span></h1>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowSidebar((v) => !v)} className="btn-ghost !py-2 !px-3 !text-xs" data-testid="chat-history-toggle">
              <ChevronLeft className={`w-3.5 h-3.5 transition-transform ${showSidebar ? "" : "rotate-180"}`} /> History
            </button>
            <button onClick={newChat} className="btn-primary !py-2 !px-3 !text-xs" data-testid="chat-new">
              <MessageSquarePlus className="w-3.5 h-3.5" /> New
            </button>
          </div>
        </div>

        <div className="flex-1 flex gap-4 min-h-0">
          {showSidebar && (
            <aside className="w-64 shrink-0 glass rounded-3xl p-3 overflow-y-auto" data-testid="chat-sidebar">
              <div className="text-[10px] font-mono uppercase tracking-widest text-lime mb-2">Your chats</div>
              {sessions.map((s) => (
                <button key={s.session_id} onClick={() => { setSessionId(s.session_id); setShowSidebar(false); }}
                  data-testid={`chat-session-${s.session_id.slice(-8)}`}
                  className={`block w-full text-left rounded-2xl px-3 py-2 mb-1 text-xs ${s.session_id === sessionId ? "bg-lime/20 text-lime" : "text-muted hover:bg-white/5"}`}>
                  <div className="font-mono truncate">{s.preview}</div>
                  <div className="text-[9px] text-muted mt-0.5">{new Date(s.last_at).toLocaleString()} · {s.count} msg</div>
                </button>
              ))}
              {sessions.length === 0 && <div className="text-[10px] font-mono text-muted p-3">No past chats yet.</div>}
            </aside>
          )}

          <div className="flex-1 flex flex-col min-w-0 glass rounded-3xl overflow-hidden">
            <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
              {messages.length === 0 && (
                <div className="text-center text-muted font-mono text-sm py-10" data-testid="chat-empty-state">
                  <Sparkles className="w-8 h-8 mx-auto text-lime mb-2" />
                  Say hi to Genie. Ask about events, artists, after-parties in your city.
                  <div className="flex flex-wrap justify-center gap-2 mt-5" data-testid="chat-suggested-prompts">
                    {[
                      "What's on this weekend in Delhi?",
                      "Find me a chill comedy night",
                      "Trips near Bhubaneswar in Nov",
                    ].map((p) => (
                      <button key={p} onClick={() => { setInput(p); }} data-testid={`chat-prompt-${p.slice(0,10).replace(/\s+/g,"-").toLowerCase()}`}
                        className="px-3 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-widest bg-lime/10 text-lime hover:bg-lime/20 border border-lime/30">{p}</button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.role}-${i}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-lime text-ink font-medium" : "bg-white/5 border border-white/10 text-text"}`}>
                    {m.content}
                    {m.role === "assistant" && m.streaming && (
                      <span className="inline-block w-1.5 h-4 align-middle ml-0.5 bg-lime animate-pulse rounded-sm" aria-hidden="true" />
                    )}
                    {m.role === "assistant" && Array.isArray(m.events) && m.events.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-white/10 space-y-2" data-testid={`chat-events-${i}`}>
                        {m.events.slice(0, 5).map((e) => (
                          <a
                            key={e.id}
                            href={e.slug ? `/e/${e.slug}` : `/events/${e.id}`}
                            data-testid={`chat-event-${e.id}`}
                            className="flex items-stretch gap-3 rounded-xl bg-lime/10 border border-lime/30 hover:bg-lime/20 overflow-hidden transition-colors"
                          >
                            <div className="w-16 shrink-0 bg-ink overflow-hidden">
                              {e.banner ? (
                                <img src={e.banner} alt={e.title} loading="lazy"
                                     className="w-full h-full object-cover"
                                     data-testid={`chat-event-thumb-${e.id}`} />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-lime/30 to-purple/30">
                                  <Sparkles className="w-5 h-5 text-lime" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0 py-2 pr-3 flex flex-col justify-between">
                              <div className="font-display uppercase text-xs leading-tight truncate">{e.title}</div>
                              <div className="text-[10px] font-mono text-muted flex items-center gap-2 mt-1 truncate">
                                <span className="inline-flex items-center gap-1"><CalIcon className="w-3 h-3" />{(e.date || "").slice(0,10)}</span>
                                <span className="inline-flex items-center gap-1 truncate"><MapPin className="w-3 h-3" />{e.city || e.location}</span>
                              </div>
                              <div className="text-[10px] font-mono uppercase tracking-widest text-lime mt-1">Book on Eventom →</div>
                            </div>
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {busy && messages[messages.length - 1]?.streaming !== true && (
                <div className="flex justify-start"><div className="rounded-2xl px-4 py-3 bg-white/5 border border-white/10"><Loader2 className="w-4 h-4 animate-spin text-lime" /></div></div>
              )}
            </div>
            <form onSubmit={send} className="border-t border-white/10 p-3 flex items-center gap-2">
              <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask Genie anything about events, artists, parties…"
                className="input-dark flex-1 !py-3" data-testid="chat-input" disabled={busy} />
              <button disabled={busy || !input.trim()} className="btn-primary !py-3 !px-4" data-testid="chat-send"
                style={{ background: "linear-gradient(135deg, #00D4FF, #7B2FFF)" }}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </form>
          </div>
        </div>
      </section>
    </Layout>
  );
}
